/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Utilidad transversal para construir el `searchsBy` de una BÚSQUEDA LIBRE por término,
 * evaluando el TIPO de dato de cada columna para elegir el operador correcto y uniendo
 * SIEMPRE las condiciones con `OR` (búsqueda por cualquier campo de la tabla).
 *
 * Reglas por tipo (alineadas con el NativeSearchQueryBuilder del backend):
 * - `text` / `enum`: `MATCH` (parcial, case-insensitive).
 * - `number`: `EQUAL`, solo si el término es numérico (un MATCH de texto sobre una columna
 *   numérica nunca matchea y, con AND, anularía toda la búsqueda).
 * - `boolean`: `EQUAL` `true`/`false`, solo si el término es un sí/no reconocible.
 *
 * El `linkOperator: 'OR'` es obligatorio: sin él el backend une con AND y basta una columna
 * que no matchee (p. ej. el código numérico) para vaciar el resultado.
 *
 * Con una excepción: la ÚLTIMA cláusula va SIN operador, porque es la que CIERRA el grupo. El
 * `NativeSearchQueryBuilder` agrupa los criterios consecutivos marcados con `OR` y cierra el grupo
 * en el primero que no lo marca. Marcarla también dejaría el grupo abierto y absorbería los
 * criterios propios de la pantalla, que viajan con `AND` detrás.
 */
export type SearchFieldType = 'text' | 'enum' | 'number' | 'boolean';

/** Definición de una columna buscable: su clave en la vista y su tipo de dato. */
export interface SearchableField {
  key: string;
  type: SearchFieldType;
}

/** Cláusula de búsqueda lista para enviar en `searchsBy`. */
export interface SearchClause {
  key: string;
  operation:
    | 'MATCH'
    | 'EQUAL'
    | 'GREATER_THAN'
    | 'LESS_THAN_EQUAL'
    | 'LESS_THAN'
    | 'GREATER_THAN_EQUAL';
  value: string | number;
  linkOperator?: 'AND' | 'OR';
}

const TRUE_TOKENS = ['si', 'sí', 's', 'yes', 'true', '1'];
const FALSE_TOKENS = ['no', 'n', 'false', '0'];

/**
 * Construye las cláusulas `searchsBy` (unidas con OR) para un término libre sobre los campos dados.
 *
 * @param term   término de búsqueda escrito por el usuario (puede venir vacío/undefined).
 * @param fields columnas buscables con su tipo de dato.
 * @returns arreglo de cláusulas; vacío si no hay término o ninguna columna aplica.
 */
export function buildSearchByTerm(
  term: string | undefined | null,
  fields: SearchableField[],
): SearchClause[] {
  const value = (term ?? '').trim();
  if (!value) return [];

  const isNumeric = /^\d+$/.test(value);
  const lower = value.toLowerCase();
  const boolValue = TRUE_TOKENS.includes(lower)
    ? 'true'
    : FALSE_TOKENS.includes(lower)
      ? 'false'
      : null;

  const clauses: SearchClause[] = [];
  for (const field of fields) {
    switch (field.type) {
      case 'text':
      case 'enum':
        clauses.push({ key: field.key, operation: 'MATCH', value, linkOperator: 'OR' });
        break;
      case 'number':
        if (isNumeric) {
          clauses.push({ key: field.key, operation: 'EQUAL', value, linkOperator: 'OR' });
        }
        break;
      case 'boolean':
        if (boolValue !== null) {
          clauses.push({
            key: field.key,
            operation: 'EQUAL',
            value: boolValue,
            linkOperator: 'OR',
          });
        }
        break;
    }
  }

  if (clauses.length > 0) {
    const lastClause = clauses[clauses.length - 1];
    const { linkOperator, ...clauseWithoutLinkOperator } = lastClause;
    clauses[clauses.length - 1] = clauseWithoutLinkOperator;
  }

  return clauses;
}
